import {AggregatedStoppageRcfaAvailability} from '@hconnect/common/stoppages/types/stoppage.types'
import {CardLinkWrapper} from '@hconnect/uikit'
import {CardBox, customThemeConstants} from '@hconnect/uikit/src/lib2'
import {ChevronRight} from '@mui/icons-material'
import {Box, Grid, Stack, Typography, useTheme} from '@mui/material'
import {ChartOptions} from 'chart.js'
import React, {useMemo} from 'react'
import {useTranslation} from 'react-i18next'
import {Link as RouterLink} from 'react-router'

import {ProgressDoughnutChart} from '../components'
import {DOWNTIME_ANALYTICS_WIDGET_NAMESPACE} from '../consts'

const RCFA_EXISTING_COLOR = customThemeConstants().palette.barChartDefault
const MANDATORY_RCFA_COLOR = customThemeConstants().palette.buttonFillColor

const chartOptions: ChartOptions<'doughnut'> = {
  cutout: '78%',
  events: [],
  responsive: true
}

interface RcfaCompletionProps {
  rcfaData: AggregatedStoppageRcfaAvailability
  onClick: () => void
  stoppageListHref?: string
  stoppageListTo?: string
}

export const RcfaCompletionContainer: React.FC<RcfaCompletionProps> = ({
  rcfaData,
  onClick,
  stoppageListHref,
  stoppageListTo
}) => {
  const theme = useTheme()
  const {t} = useTranslation(DOWNTIME_ANALYTICS_WIDGET_NAMESPACE)

  const chartCenterText = useMemo(() => {
    return {
      id: 'chart_center_text',
      afterDatasetsDraw(chart) {
        const {ctx} = chart
        const xBase = chart.getDatasetMeta(0).data[0].x
        const yBase = chart.getDatasetMeta(0).data[0].y
        ctx.save()

        ctx.font = 'normal 300 28px/34 Inter'
        ctx.textAlign = 'center'
        ctx.fillStyle = theme.palette.text.primary
        ctx.textBaseLine = 'middle'
        if (rcfaData) {
          ctx.fillText(
            `${rcfaData.longKilnStoppageRcfaCount}/${rcfaData.longKilnStoppageCount}`,
            xBase,
            yBase - 5
          )
        }
        ctx.font = 'normal 400 12px/16 Inter'
        ctx.textAlign = 'center'
        ctx.fillStyle = theme.palette.secondary.main
        ctx.textBaseLine = 'middle'
        ctx.fillText(t('rcfaCompletion.mandatoryRCFAs'), xBase, yBase + 20)
      }
    }
  }, [t, theme.palette.secondary.main, theme.palette.text.primary, rcfaData])

  const cardLinkProps = stoppageListTo
    ? {
        component: RouterLink,
        to: stoppageListTo
      }
    : {
        href: stoppageListHref
      }

  return (
    <CardLinkWrapper onClick={onClick} data-test-id="rcfa-card-link-to-maintain" {...cardLinkProps}>
      <CardBox>
        <Box mb={2} display="flex" flexDirection="row">
          <Box flex={1} mt={-0.5}>
            <Typography variant="h3" mb={5}>
              {t('rcfaCompletion.title')}
            </Typography>
          </Box>
          <Box mr={-1} className={'card-chevron'}>
            <ChevronRight />
          </Box>
        </Box>
        <Box height={190} display="flex" justifyContent="center" alignItems="center">
          {rcfaData && (
            <ProgressDoughnutChart
              data={[rcfaData.longKilnStoppageRcfaCount, rcfaData.longKilnStoppageCount]}
              backgroundList={[MANDATORY_RCFA_COLOR, RCFA_EXISTING_COLOR]}
              pluginsList={[chartCenterText]}
              options={chartOptions}
            />
          )}
        </Box>
        <Grid display="flex" justifyContent="space-between" flexWrap="wrap" marginTop={2}>
          <Grid item flex={1}>
            <RcfaStatisticBlock
              title={t('rcfaCompletion.missingRCFAs')}
              value={rcfaData.longKilnStoppageCount - rcfaData.longKilnStoppageRcfaCount}
            />
          </Grid>
          <Grid item flex={1}>
            <RcfaStatisticBlock
              title={t('rcfaCompletion.optionalRCFAs')}
              value={rcfaData.stoppageRcfaCount - rcfaData.longKilnStoppageRcfaCount}
            />
          </Grid>
          <Grid item flex={1}>
            <RcfaStatisticBlock
              title={t('rcfaCompletion.kilnStoppages')}
              value={rcfaData.kilnStoppageCount}
            />
          </Grid>
        </Grid>
      </CardBox>
    </CardLinkWrapper>
  )
}

interface RcfaListItemProps {
  title: string
  value: string | number
}

const RcfaStatisticBlock: React.FC<RcfaListItemProps> = ({title, value}) => {
  const theme = useTheme()
  return (
    <Stack
      direction="column"
      borderRadius="8px"
      border={`1px solid ${theme.palette.grey[200]}`}
      margin={theme.spacing(1)}
      padding={theme.spacing(2)}
      boxShadow={theme.shadows[4]}
    >
      <Typography textAlign="center" variant="h1" mb={1}>
        {value}
      </Typography>
      <Typography textAlign="center" variant="caption">
        {title}
      </Typography>
    </Stack>
  )
}
